"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { ClimateChart } from "@/components/tools/earth-science/climate-chart";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type ClimateStats,
  climateAxes,
  climateStats,
  convertPrecipitation,
  convertTemperature,
  type PrecipitationUnit,
  parseClimateTable,
  type TemperatureUnit,
} from "@/lib/tool-kit/earth-science/climate";

/** English strings. A translated content file overrides these through `ui`. */
export const CLIMATE_GRAPH_MAKER_STRINGS = {
  dataLabel:
    "Your climate data — one month per line: month, temperature, precipitation",
  dataHint:
    "Twelve lines like Jan, 4.5, 55. The month name is optional, and a first line without numbers names the columns. Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas.",
  exampleData:
    "Month, Temperature, Precipitation\nJan, 4.5, 55\nFeb, 4.8, 41\nMar, 7.2, 42\nApr, 9.8, 44\nMay, 13.2, 49\nJun, 16.3, 49\nJul, 18.6, 45\nAug, 18.3, 52\nSep, 15.3, 49\nOct, 11.5, 68\nNov, 7.5, 59\nDec, 5.0, 55",
  exampleTitle:
    "Climate graph for an example temperate station (illustrative data)",
  dataTempUnit: "Your temperatures are in",
  dataPrecipUnit: "Your precipitation is in",
  drawTempUnit: "Draw temperature in",
  drawPrecipUnit: "Draw precipitation in",
  unitC: "degrees Celsius (°C)",
  unitF: "degrees Fahrenheit (°F)",
  symbolC: "°C",
  symbolF: "°F",
  symbolMm: "mm",
  symbolIn: "in",
  unitMm: "millimetres (mm)",
  unitIn: "inches (in)",
  monthLabels: "Month labels",
  labelsNames: "As entered (Jan–Dec if missing)",
  labelsInitials: "Initials (J F M A M J J A S O N D)",
  linked: "Walter–Lieth scale: 10 °C = 20 mm on the two axes",
  linkedHint:
    "Ties the right axis to the left at 2 mm per °C, so a month where the line runs above the bars is dry. Available when drawing in °C and mm.",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  colorLabel: "Color of the precipitation bars",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the values on the bars and points",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  columnMonth: "Month",
  columnTemperature: "Temperature ({unit})",
  columnPrecipitation: "Precipitation ({unit})",
  tempTitle: "Temperature ({unit})",
  precipTitle: "Precipitation ({unit})",
  months: {
    jan: "Jan",
    feb: "Feb",
    mar: "Mar",
    apr: "Apr",
    may: "May",
    jun: "Jun",
    jul: "Jul",
    aug: "Aug",
    sep: "Sep",
    oct: "Oct",
    nov: "Nov",
    dec: "Dec",
  },
  initials: {
    jan: "J",
    feb: "F",
    mar: "M",
    apr: "A",
    may: "M",
    jun: "J",
    jul: "J",
    aug: "A",
    sep: "S",
    oct: "O",
    nov: "N",
    dec: "D",
  },
  chartLabel: "Climate graph",
  chartDescription:
    "Climate graph of {count}. Mean temperature {mean} {tempUnit}, from {coldest} in {coldMonth} to {warmest} in {warmMonth}. Total precipitation {total} {precipUnit}, wettest in {wetMonth} with {wettest} {precipUnit}.",
  monthCount: "one:{count} month|other:{count} months",
  statMean: "Mean annual temperature",
  statTotal: "Total annual precipitation",
  statRange: "Annual temperature range",
  statWarmest: "Warmest month",
  statColdest: "Coldest month",
  statWettest: "Wettest month",
  statDriest: "Driest month",
  enterData:
    "Enter at least two months with a temperature and a precipitation to draw the graph.",
  notTwelve:
    "A climate graph shows twelve months and you entered {count}. The annual figures use the months given.",
  tooMany: "Only the first twelve months are drawn.",
  skipped:
    "one:Line {lines} has no usable numbers and was left out.|other:Lines {lines} have no usable numbers and were left out.",
  incomplete:
    "one:{count} month is missing its temperature or its precipitation and was left out.|other:{count} months are missing their temperature or precipitation and were left out.",
  invalidCells:
    "one:{count} cell is not a number and was read as missing.|other:{count} cells are not numbers and were read as missing.",
} as const;

type Strings = typeof CLIMATE_GRAPH_MAKER_STRINGS;
type MonthKey = keyof Strings["months"];

const MONTH_KEYS: MonthKey[] = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

interface Month {
  label: string;
  temperature: number;
  precipitation: number;
}

function monthLabel(
  typed: string | null,
  index: number,
  initials: boolean,
  strings: Strings
): string {
  const key = MONTH_KEYS[index];
  if (initials && key) {
    return strings.initials[key];
  }
  return typed ?? (key ? strings.months[key] : String(index + 1));
}

function UnitSelects<V extends string>({
  data,
  draw,
  onData,
  onDraw,
  dataLabel,
  drawLabel,
  idPrefix,
  options,
}: {
  data: V;
  draw: V;
  onData: (value: V) => void;
  onDraw: (value: V) => void;
  dataLabel: string;
  drawLabel: string;
  idPrefix: string;
  options: { value: V; label: string }[];
}) {
  return (
    <>
      <SelectField
        id={`${idPrefix}-data`}
        label={dataLabel}
        onChange={onData}
        options={options}
        value={data}
      />
      <SelectField
        id={`${idPrefix}-draw`}
        label={drawLabel}
        onChange={onDraw}
        options={options}
        value={draw}
      />
    </>
  );
}

export default function ClimateGraphMakerWidget() {
  const strings = useToolUi(CLIMATE_GRAPH_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [dataTemp, setDataTemp] = useState<TemperatureUnit>("C");
  const [drawTemp, setDrawTemp] = useState<TemperatureUnit>("C");
  const [dataPrecip, setDataPrecip] = useState<PrecipitationUnit>("mm");
  const [drawPrecip, setDrawPrecip] = useState<PrecipitationUnit>("mm");
  const [initials, setInitials] = useState<"names" | "initials">("names");
  const [linkedChoice, setLinkedChoice] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "skyBlue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseClimateTable(raw);
  const complete = table.months.filter(
    (month) => month.temperature !== null && month.precipitation !== null
  );
  const incomplete = table.months.length - complete.length;
  const drawn: Month[] = complete.slice(0, 12).map((month, index) => ({
    label: monthLabel(month.label, index, initials === "initials", strings),
    temperature: convertTemperature(month.temperature ?? 0, dataTemp, drawTemp),
    precipitation: convertPrecipitation(
      month.precipitation ?? 0,
      dataPrecip,
      drawPrecip
    ),
  }));
  const canLink = drawTemp === "C" && drawPrecip === "mm";
  const linked = linkedChoice && canLink;
  const tempUnit = drawTemp === "C" ? strings.symbolC : strings.symbolF;
  const precipUnit = drawPrecip === "mm" ? strings.symbolMm : strings.symbolIn;
  const tempTitle = t(strings.tempTitle, { unit: tempUnit });
  const precipTitle = t(strings.precipTitle, { unit: precipUnit });
  const temps = drawn.map((month) => month.temperature);
  const precips = drawn.map((month) => month.precipitation);
  const stats = climateStats(temps, precips);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="cgm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <UnitSelects
          data={dataTemp}
          dataLabel={strings.dataTempUnit}
          draw={drawTemp}
          drawLabel={strings.drawTempUnit}
          idPrefix="cgm-temp"
          onData={setDataTemp}
          onDraw={setDrawTemp}
          options={[
            { value: "C", label: strings.unitC },
            { value: "F", label: strings.unitF },
          ]}
        />
        <UnitSelects
          data={dataPrecip}
          dataLabel={strings.dataPrecipUnit}
          draw={drawPrecip}
          drawLabel={strings.drawPrecipUnit}
          idPrefix="cgm-precip"
          onData={setDataPrecip}
          onDraw={setDrawPrecip}
          options={[
            { value: "mm", label: strings.unitMm },
            { value: "in", label: strings.unitIn },
          ]}
        />
        <SelectField
          id="cgm-labels"
          label={strings.monthLabels}
          onChange={setInitials}
          options={[
            { value: "names", label: strings.labelsNames },
            { value: "initials", label: strings.labelsInitials },
          ]}
          value={initials}
        />
        {canLink ? (
          <div className="space-y-2">
            <Checkbox
              checked={linkedChoice}
              label={strings.linked}
              onChange={setLinkedChoice}
            />
            <p className={HINT}>{strings.linkedHint}</p>
          </div>
        ) : null}
      </div>
      <Notices
        count={drawn.length}
        fmt={fmt}
        incomplete={incomplete}
        invalid={table.invalidCells}
        locale={locale}
        skipped={table.skipped}
        strings={strings}
        total={complete.length}
      />
      {drawn.length >= 2 && stats ? (
        <>
          <ChartFigure
            apa={style.apa}
            data={toTsv([
              [strings.columnMonth, tempTitle, precipTitle],
              ...drawn.map((m) => [m.label, m.temperature, m.precipitation]),
            ])}
            figureNumber={style.figureNumber}
            filename={chartFilename(style.title, "climate-graph")}
            labels={strings}
            note={style.note}
            table={{
              head: [
                strings.columnMonth,
                t(strings.columnTemperature, { unit: tempUnit }),
                t(strings.columnPrecipitation, { unit: precipUnit }),
              ],
              rows: drawn.map((m) => [
                m.label,
                fmt(m.temperature, { maximumFractionDigits: 1 }),
                fmt(m.precipitation, { maximumFractionDigits: 1 }),
              ]),
            }}
            title={style.title}
          >
            <ClimateChart
              axes={climateAxes(temps, precips, linked)}
              description={describe(
                drawn,
                stats,
                strings,
                locale,
                fmt,
                tempUnit,
                precipUnit
              )}
              fmt={fmt}
              id="cgm-chart"
              label={style.title.trim() || strings.chartLabel}
              months={drawn}
              precipTitle={precipTitle}
              style={style}
              tempTitle={tempTitle}
            />
          </ChartFigure>
          <StatsGrid
            fmt={fmt}
            months={drawn}
            precipUnit={precipUnit}
            stats={stats}
            strings={strings}
            tempUnit={tempUnit}
          />
        </>
      ) : (
        <Notice>{strings.enterData}</Notice>
      )}
      <ChartStyleFields
        colorLabels={strings}
        idPrefix="cgm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

function describe(
  months: Month[],
  stats: ClimateStats,
  strings: Strings,
  locale: string,
  fmt: Fmt,
  tempUnit: string,
  precipUnit: string
): string {
  const one = { maximumFractionDigits: 1 };
  return t(strings.chartDescription, {
    count: tPlural(strings.monthCount, locale, months.length, {
      count: fmt(months.length),
    }),
    mean: fmt(stats.meanTemperature, one),
    tempUnit,
    coldest: fmt(months[stats.coldest].temperature, one),
    coldMonth: months[stats.coldest].label,
    warmest: fmt(months[stats.warmest].temperature, one),
    warmMonth: months[stats.warmest].label,
    total: fmt(stats.totalPrecipitation, one),
    precipUnit,
    wetMonth: months[stats.wettest].label,
    wettest: fmt(months[stats.wettest].precipitation, one),
  });
}

function StatsGrid({
  stats,
  months,
  strings,
  fmt,
  tempUnit,
  precipUnit,
}: {
  stats: ClimateStats;
  months: Month[];
  strings: Strings;
  fmt: Fmt;
  tempUnit: string;
  precipUnit: string;
}) {
  const one = { maximumFractionDigits: 1 };
  const temp = (value: number) => `${fmt(value, one)} ${tempUnit}`;
  const precip = (value: number) => `${fmt(value, one)} ${precipUnit}`;
  const month = (index: number, value: string) =>
    `${months[index].label} (${value})`;
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={strings.statMean}
        value={temp(stats.meanTemperature)}
      />
      <StatCard
        emphasis
        label={strings.statTotal}
        value={precip(stats.totalPrecipitation)}
      />
      <StatCard label={strings.statRange} value={temp(stats.range)} />
      <StatCard
        label={strings.statWarmest}
        value={month(stats.warmest, temp(months[stats.warmest].temperature))}
      />
      <StatCard
        label={strings.statColdest}
        value={month(stats.coldest, temp(months[stats.coldest].temperature))}
      />
      <StatCard
        label={strings.statWettest}
        value={month(
          stats.wettest,
          precip(months[stats.wettest].precipitation)
        )}
      />
      <StatCard
        label={strings.statDriest}
        value={month(stats.driest, precip(months[stats.driest].precipitation))}
      />
    </StatGrid>
  );
}

function Notices({
  count,
  total,
  incomplete,
  skipped,
  invalid,
  strings,
  locale,
  fmt,
}: {
  count: number;
  total: number;
  incomplete: number;
  skipped: number[];
  invalid: number;
  strings: Strings;
  locale: string;
  fmt: Fmt;
}) {
  const notes: string[] = [];
  if (skipped.length > 0) {
    notes.push(
      tPlural(strings.skipped, locale, skipped.length, {
        lines: skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (incomplete > 0) {
    notes.push(
      tPlural(strings.incomplete, locale, incomplete, {
        count: fmt(incomplete),
      })
    );
  }
  if (invalid > 0) {
    notes.push(
      tPlural(strings.invalidCells, locale, invalid, { count: fmt(invalid) })
    );
  }
  if (total > 12) {
    notes.push(strings.tooMany);
  } else if (count >= 2 && count !== 12) {
    notes.push(t(strings.notTwelve, { count: fmt(count) }));
  }
  if (notes.length === 0) {
    return null;
  }
  return (
    <div aria-live="polite" className="space-y-2">
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
    </div>
  );
}
